import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { Users, Search, Loader2, CheckCircle2, Calendar, Ticket, Download, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(d) {
  if (!d) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  if (m) {
    const local = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    try { return local.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return d; }
  }
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return d; }
}

export default function AttendeeCatalog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [eventFilter, setEventFilter] = useState(() => new Set());      // Set of event_ids
  const [dateFilter, setDateFilter] = useState(() => new Set());        // Set of YYYY-MM-DD
  const [statusFilter, setStatusFilter] = useState("");                 // "in", "out", or ""
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/attendees/all`, { withCredentials: true });
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load attendee catalog");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rowKey = (r) => `${r.event_id}-${r.attendee_id}`;

  // Distinct events (id → name) and dates surfaced from the dataset.
  const eventOptions = useMemo(() => {
    const seen = new Map();
    for (const r of rows) {
      if (r.event_id && !seen.has(r.event_id)) {
        seen.set(r.event_id, r.event_name || "(deleted event)");
      }
    }
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const dateOptions = useMemo(() => {
    const set = new Set();
    for (const r of rows) if (r.event_date) set.add(r.event_date);
    return Array.from(set).sort().reverse();
  }, [rows]);

  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => rows.filter((r) => {
    if (q && !`${r.first_name || ""} ${r.last_name || ""}`.toLowerCase().includes(q)) return false;
    if (eventFilter.size > 0 && !eventFilter.has(r.event_id)) return false;
    if (dateFilter.size > 0 && !dateFilter.has(r.event_date)) return false;
    if (statusFilter === "in" && !r.checked_in_at) return false;
    if (statusFilter === "out" && r.checked_in_at) return false;
    return true;
  }), [rows, q, eventFilter, dateFilter, statusFilter]);

  const duplicateNames = useMemo(() => {
    const counts = new Map();
    for (const r of rows) {
      const key = `${(r.first_name || "").trim().toLowerCase()}|${(r.last_name || "").trim().toLowerCase()}`;
      if (key === "|") continue;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    const dups = new Set();
    counts.forEach((n, k) => { if (n > 1) dups.add(k); });
    return dups;
  }, [rows]);

  // Bulk-select logic: scoped to the currently-filtered view
  const filteredKeys = useMemo(() => filtered.map(rowKey), [filtered]);
  const selectedInFiltered = filteredKeys.filter((k) => selectedKeys.has(k));
  const allSelected = filteredKeys.length > 0 && selectedInFiltered.length === filteredKeys.length;
  const someSelected = !allSelected && selectedInFiltered.length > 0;

  const toggleOne = (k) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const toggleAllFiltered = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allSelected) filteredKeys.forEach((k) => next.delete(k));
      else filteredKeys.forEach((k) => next.add(k));
      return next;
    });
  };
  const clearAll = () => setSelectedKeys(new Set());

  const clearFilters = () => {
    setSearch(""); setEventFilter(new Set()); setDateFilter(new Set()); setStatusFilter("");
  };

  // Export: prefer user's checkbox selection if any, else fall back to the
  // current filtered view.
  const handleExport = () => {
    const useSelection = selectedKeys.size > 0;
    const exportRows = useSelection
      ? rows.filter((r) => selectedKeys.has(rowKey(r)))
      : filtered;
    if (exportRows.length === 0) { toast.error("Nothing to export"); return; }
    const header = ["First Name", "Last Name", "Event", "Event Date", "Passport", "Checked In", "Checked In At"];
    const data = exportRows.map((r) => [
      r.first_name || "",
      r.last_name || "",
      r.event_name || "",
      r.event_date || "",
      r.passport_name || "",
      r.checked_in_at ? "Yes" : "No",
      r.checked_in_at || "",
    ]);
    const csv = [header, ...data].map((row) => row.map((c) => {
      const s = String(c);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = useSelection ? "attendee_catalog_selected.csv" : "attendee_catalog_filtered.csv";
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const filtersActive = !!(search || eventFilter.size > 0 || dateFilter.size > 0 || statusFilter);

  return (
    <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-5 overflow-hidden" data-testid="attendee-catalog">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="min-w-0">
          <h3 className="font-heading text-lg font-bold text-white inline-flex items-center gap-2">
            <Users className="h-4 w-4 text-[var(--primary-color)]" /> Attendee Catalog
          </h3>
          <p className="text-xs font-body text-[#6E7787] mt-0.5">
            Read-only list of every attendee across every event.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap shrink-0">
          <span className="text-xs font-body text-[#6E7787] inline-flex items-center gap-1.5">
            <Users className="h-3 w-3" /> {filtered.length} of {rows.length}
            {selectedKeys.size > 0 ? <> · <span className="text-emerald-300">{selectedKeys.size} selected</span></> : null}
          </span>
          <Button
            onClick={handleExport}
            disabled={filtered.length === 0 && selectedKeys.size === 0}
            variant="outline"
            size="sm"
            className="gap-1.5 border-white/15 text-white bg-transparent hover:bg-white/5"
            data-testid="attendee-catalog-export"
          >
            <Download className="h-3.5 w-3.5" />
            {selectedKeys.size > 0 ? `Export Selected (${selectedKeys.size})` : "Export Filtered"}
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative min-w-[200px] flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6E7787]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search attendee name…"
            data-testid="attendee-catalog-search"
            className="pl-10 h-9 bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787]"
          />
        </div>
        <MultiSelectFilter
          label="events"
          allLabel="All events"
          options={eventOptions.map((e) => ({ value: e.id, label: e.name }))}
          selected={eventFilter}
          onChange={setEventFilter}
          testid="attendee-catalog-filter-event"
        />
        <MultiSelectFilter
          label="dates"
          allLabel="All dates"
          options={dateOptions.map((d) => ({ value: d, label: fmtDate(d) }))}
          selected={dateFilter}
          onChange={setDateFilter}
          testid="attendee-catalog-filter-date"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md bg-[#0A0C10] border border-white/10 text-white text-xs font-body px-2 focus:outline-none focus:border-white/30"
          data-testid="attendee-catalog-filter-status"
        >
          <option value="">Any check-in status</option>
          <option value="in">Checked in</option>
          <option value="out">Not checked in</option>
        </select>
        {filtersActive ? (
          <button
            onClick={clearFilters}
            className="text-xs font-body text-[#A1A9B8] underline hover:text-white"
            data-testid="attendee-catalog-clear-filters"
          >
            Clear filters
          </button>
        ) : null}
        {selectedKeys.size > 0 ? (
          <button
            onClick={clearAll}
            className="text-xs font-body text-[#A1A9B8] underline hover:text-white"
            data-testid="attendee-catalog-clear-selection"
          >
            Clear selection
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="py-12 flex items-center justify-center text-[#6E7787]">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm font-body text-[#6E7787] py-10 text-center">
          {rows.length === 0 ? "No attendees yet across any events." : "No attendees match your filters."}
        </p>
      ) : (
        <div className="border border-white/10 rounded-lg overflow-hidden">
          <div className="hidden sm:grid grid-cols-12 gap-3 px-4 py-2.5 border-b border-white/10 text-[10px] uppercase tracking-wider text-[#6E7787] font-body bg-white/[0.02]">
            <div className="col-span-1 flex items-center">
              <Checkbox
                checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                onCheckedChange={toggleAllFiltered}
                className="h-4 w-4 border-2 border-white/60 bg-[#0A0C10] data-[state=checked]:bg-[var(--primary-color)] data-[state=checked]:border-[var(--primary-color)]"
                data-testid="attendee-catalog-select-all"
              />
            </div>
            <div className="col-span-3">Name</div>
            <div className="col-span-3">Event</div>
            <div className="col-span-2">Event Date</div>
            <div className="col-span-2">Passport</div>
            <div className="col-span-1">Checked In</div>
          </div>
          <ul className="divide-y divide-white/5">
            {filtered.map((r) => {
              const k = rowKey(r);
              const isSelected = selectedKeys.has(k);
              return (
                <li
                  key={k}
                  className={`sm:grid sm:grid-cols-12 sm:gap-3 px-4 py-2.5 ${isSelected ? "bg-[var(--primary-color)]/5" : "hover:bg-white/[0.02]"}`}
                  data-testid={`catalog-row-${r.attendee_id}`}
                >
                  <div className="sm:col-span-1 flex items-center">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleOne(k)}
                      className="h-4 w-4 border-2 border-white/60 bg-[#0A0C10] data-[state=checked]:bg-[var(--primary-color)] data-[state=checked]:border-[var(--primary-color)]"
                      data-testid={`catalog-select-${r.attendee_id}`}
                    />
                  </div>
                  <div className="sm:col-span-3 text-sm font-body text-white truncate inline-flex items-center gap-2 min-w-0 mt-1 sm:mt-0">
                    <span className="truncate">
                      {((r.first_name || "") + " " + (r.last_name || "")).trim() || "(no name)"}
                    </span>
                    {duplicateNames.has(`${(r.first_name || "").trim().toLowerCase()}|${(r.last_name || "").trim().toLowerCase()}`) ? (
                      <span
                        className="shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-400/30 font-body font-semibold"
                        title="This exact name appears on more than one event"
                        data-testid={`catalog-duplicate-${r.attendee_id}`}
                      >
                        Duplicate
                      </span>
                    ) : null}
                  </div>
                  <div className="sm:col-span-3 text-xs font-body text-[#D5D9E3] truncate inline-flex items-center gap-1 min-w-0 mt-1 sm:mt-0">
                    <Calendar className="h-3 w-3 text-[#6E7787] shrink-0" />
                    <span className="truncate">{r.event_name || "(deleted event)"}</span>
                  </div>
                  <div className="sm:col-span-2 text-xs font-body text-[#A1A9B8] tabular-nums mt-1 sm:mt-0">
                    {fmtDate(r.event_date) || "—"}
                  </div>
                  <div className="sm:col-span-2 text-xs font-body text-[#D5D9E3] truncate inline-flex items-center gap-1 min-w-0 mt-1 sm:mt-0">
                    {r.passport_name ? (
                      <>
                        <Ticket className="h-3 w-3 text-[var(--primary-color)] shrink-0" />
                        <span className="truncate">{r.passport_name}</span>
                      </>
                    ) : <span className="text-[#6E7787]">—</span>}
                  </div>
                  <div className="sm:col-span-1 text-xs font-body mt-1 sm:mt-0">
                    {r.checked_in_at ? (
                      <span className="inline-flex items-center gap-1 text-emerald-300">
                        <CheckCircle2 className="h-3 w-3" /> Yes
                      </span>
                    ) : (
                      <span className="text-[#6E7787]">No</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function MultiSelectFilter({ label, allLabel, options, selected, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange(next);
  };
  const clear = () => onChange(new Set());

  const summary = selected.size === 0
    ? allLabel
    : selected.size === 1
      ? (options.find((o) => o.value === Array.from(selected)[0])?.label || `1 ${label}`)
      : `${selected.size} ${label}`;

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="h-9 rounded-md bg-[#0A0C10] border border-white/10 text-white text-xs font-body px-2.5 focus:outline-none focus:border-white/30 inline-flex items-center gap-1.5 hover:border-white/20 max-w-[200px]"
        data-testid={testid}
      >
        <span className="truncate">{summary}</span>
        <ChevronDown className="h-3 w-3 text-[#6E7787] shrink-0" />
      </button>
      {open ? (
        <div
          className="absolute z-20 mt-1 left-0 min-w-[220px] max-w-[300px] rounded-md bg-[#11141A] border border-white/15 shadow-lg overflow-hidden"
          data-testid={`${testid}-popover`}
        >
          {options.length === 0 ? (
            <div className="px-3 py-3 text-xs font-body text-[#6E7787]">No options</div>
          ) : (
            <>
              <button
                type="button"
                onClick={clear}
                disabled={selected.size === 0}
                className="w-full px-3 py-2 text-left text-xs font-body text-[#A1A9B8] hover:bg-white/5 border-b border-white/5 disabled:opacity-40"
                data-testid={`${testid}-clear`}
              >
                Clear all
              </button>
              <ul className="max-h-[260px] overflow-y-auto">
                {options.map((o) => {
                  const isOn = selected.has(o.value);
                  return (
                    <li key={o.value}>
                      <button
                        type="button"
                        onClick={() => toggle(o.value)}
                        className="w-full px-3 py-2 text-left text-xs font-body text-white hover:bg-white/5 inline-flex items-center gap-2"
                        data-testid={`${testid}-option-${o.value}`}
                      >
                        <Checkbox
                          checked={isOn}
                          className="h-4 w-4 border-2 border-white/60 bg-[#0A0C10] data-[state=checked]:bg-[var(--primary-color)] data-[state=checked]:border-[var(--primary-color)] shrink-0 pointer-events-none"
                        />
                        <span className="truncate flex-1">{o.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
